import type { Papers } from './supabase';

type Paper = Papers;

/**
 * Adds version number to paper URLs (arXiv, medRxiv, bioRxiv)
 * Database stores URLs without version, but we need versioned URLs for correct linking.
 *
 * URL patterns:
 * - arXiv: https://arxiv.org/pdf/2503.16007.pdf → https://arxiv.org/pdf/2503.16007v1.pdf
 * - arXiv abs: https://arxiv.org/abs/2503.16007 → https://arxiv.org/abs/2503.16007v1
 * - medRxiv: https://www.medrxiv.org/content/10.1101/2025.09.24.25336349.full.pdf → .../25336349v1.full.pdf
 * - bioRxiv: https://www.biorxiv.org/content/10.1101/2025.07.24.666531.full.pdf → .../666531v1.full.pdf
 */
export function getVersionedUrl(url: string | null | undefined, version: string | null | undefined): string | null {
  if (!url) return null;
  if (!version) return url;

  // Check if version is already in the URL (e.g., v1, v2, etc.)
  // Pattern: 'v' followed by digits, before .pdf or .full or end
  const versionPattern = /v\d+(?=\.|\s|$)/i;
  if (versionPattern.test(url)) {
    return url;
  }

  // Version might already include 'v' prefix (e.g., "v1") or just be a number (e.g., "1")
  const versionSuffix = version.startsWith('v') ? version : `v${version}`;

  // arXiv URLs: insert version before .pdf or at end for /abs/ URLs
  if (url.includes('arxiv.org')) {
    if (url.endsWith('.pdf')) {
      return url.replace(/\.pdf$/, `${versionSuffix}.pdf`);
    }
    // For abstract pages (no .pdf extension)
    return url + versionSuffix;
  }

  // medRxiv/bioRxiv URLs: insert version before .full.pdf or .full
  if (url.includes('medrxiv.org') || url.includes('biorxiv.org')) {
    if (url.includes('.full.pdf')) {
      return url.replace('.full.pdf', `${versionSuffix}.full.pdf`);
    }
    if (url.includes('.full')) {
      return url.replace('.full', `${versionSuffix}.full`);
    }
    // For URLs without .full extension, append at the end
    return url + versionSuffix;
  }

  // ChinaXiv URLs: add locale=en parameter for English interface
  if (url.includes('chinaxiv.org')) {
    const separator = url.includes('?') ? '&' : '?';
    return url + separator + 'locale=en';
  }

  // For unknown URL patterns, return as-is
  return url;
}

/**
 * Shared utility to create a URL-friendly slug from text
 */
export function createSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Generates a URL-friendly slug from a paper
 */
export function generatePaperSlug(paper: Paper): string {
  // Try original title first
  const originalSlug = createSlug(paper.original_title);
  if (originalSlug.length >= 12) {
    return originalSlug;
  }

  // Try paperzilla title second
  const pzSlug = paper.paperzilla_title ? createSlug(paper.paperzilla_title) : '';
  if (pzSlug.length >= 12) {
    return pzSlug;
  }

  // Try summary of findings third
  const summarySlug = paper.summary_of_findings ? createSlug(paper.summary_of_findings) : '';
  if (summarySlug.length >= 12) {
    return summarySlug;
  }

  // If all are < 12, return the longest one
  const slugs = [
    { slug: originalSlug, length: originalSlug.length },
    { slug: pzSlug, length: pzSlug.length },
    { slug: summarySlug, length: summarySlug.length }
  ];

  return slugs.sort((a, b) => b.length - a.length)[0].slug;
}

/**
 * Generates a URL-friendly slug from a digest project name
 */
export function generateDigestSlug(projectName: string): string {
  return createSlug(projectName);
}

/**
 * Extracts the short ID from a full UUID (first 8 characters)
 */
export function getShortId(fullId: string): string {
  return fullId.split('-')[0];
}

/**
 * Generates the internal navigation URL for a paper
 */
export function getPaperUrl(paper: Paper): string {
  const shortId = paper.short_id || getShortId(paper.id);
  const slug = generatePaperSlug(paper);
  
  return `/paper/${shortId}/${slug}`;
}

/**
 * Generates the external shareable URL for a paper
 */
export function getPaperShareUrl(paper: Paper): string {
  const baseUrl = process.env.EXPO_PUBLIC_BASE_URL || 'https://paperzilla.ai';
  const shortId = paper.short_id || getShortId(paper.id);
  const slug = generatePaperSlug(paper);
  
  return `${baseUrl}/paper/${shortId}/${slug}`;
}

/**
 * Generates paper URL params for expo-router navigation
 */
export function getPaperUrlParams(paper: Paper) {
  const shortId = paper.short_id || getShortId(paper.id);
  const slug = generatePaperSlug(paper);

  return {
    pathname: '/paper/[paperId]/[...title]',
    params: {
      paperId: shortId,
      title: slug
    }
  };
}

/**
 * Generates a simple paper URL from paperId and title string
 * Useful when you don't have a full Paper object
 */
export function getSimplePaperUrl(paperId: string, title: string): string {
  const slug = createSlug(title);
  return `/p/${paperId}/${slug}`;
}

export interface StandalonePaperBackLink {
  backHref: string;
  backText?: string;
}

export interface StandalonePaperProjectContext {
  projectId?: string;
  projectPaperId?: string;
}

const STANDALONE_PAPER_ROUTE_PARAM_KEYS = ['paperId', 'title'] as const;

/**
 * Removes leaked route-param query keys from standalone paper URLs while
 * preserving intentional query params (e.g. back-link and project context).
 */
export function sanitizeStandalonePaperSearch(search: string): string {
  const rawQuery = search.startsWith('?') ? search.slice(1) : search;
  const searchParams = new URLSearchParams(rawQuery);

  STANDALONE_PAPER_ROUTE_PARAM_KEYS.forEach((key) => {
    searchParams.delete(key);
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

/**
 * Adds standalone-paper project context query params to a paper URL.
 * These params let the destination page resolve project-aware feedback context.
 */
export function attachStandalonePaperProjectContext(
  paperUrl: string,
  projectContext?: StandalonePaperProjectContext
): string {
  if (!projectContext?.projectId || !projectContext?.projectPaperId) {
    return paperUrl;
  }

  const [basePath, existingQuery = ''] = paperUrl.split('?');
  const searchParams = new URLSearchParams(existingQuery);
  searchParams.set('projectId', projectContext.projectId);
  searchParams.set('projectPaperId', projectContext.projectPaperId);

  const query = searchParams.toString();
  return query ? `${basePath}?${query}` : basePath;
}

/**
 * Adds standalone-paper back-link query params to a paper URL.
 * These params let the destination page render deterministic "back to …" navigation.
 */
export function attachStandalonePaperBackLink(
  paperUrl: string,
  backLink?: StandalonePaperBackLink
): string {
  if (!backLink?.backHref) {
    return paperUrl;
  }

  const [basePath, existingQuery = ''] = paperUrl.split('?');
  const searchParams = new URLSearchParams(existingQuery);
  searchParams.set('backHref', backLink.backHref);

  if (backLink.backText) {
    searchParams.set('backText', backLink.backText);
  }

  const query = searchParams.toString();
  return query ? `${basePath}?${query}` : basePath;
}

// ==================== DIGEST URL UTILITIES ====================

export interface DigestUrlInput {
  id: string;
  short_id?: string | null;
  project: {
    name: string;
  };
}

export interface ProjectPaperUrlInput {
  id: string;
  short_id?: string | null;
  slug?: string | null;
  paper_title?: string | null;
}

/**
 * Generates the internal navigation URL for a digest issue
 * Accepts either a digest object or individual params
 */
export function getDigestUrl(digestOrId: DigestUrlInput | string, projectName?: string): string {
  if (typeof digestOrId === 'string') {
    // Legacy: called with (issueId, projectName)
    const shortId = getShortId(digestOrId);
    const slug = generateDigestSlug(projectName!);
    return `/digest/${shortId}/${slug}`;
  }

  // Modern: called with digest object
  const shortId = digestOrId.short_id || getShortId(digestOrId.id);
  const slug = generateDigestSlug(digestOrId.project.name);
  return `/digest/${shortId}/${slug}`;
}

/**
 * Generates the external shareable URL for a digest issue
 * Accepts either a digest object or individual params
 */
export function getDigestShareUrl(digestOrId: DigestUrlInput | string, projectName?: string): string {
  const baseUrl = process.env.EXPO_PUBLIC_BASE_URL || 'https://paperzilla.ai';

  if (typeof digestOrId === 'string') {
    // Legacy: called with (issueId, projectName)
    const shortId = getShortId(digestOrId);
    const slug = generateDigestSlug(projectName!);
    return `${baseUrl}/digest/${shortId}/${slug}`;
  }

  // Modern: called with digest object
  const shortId = digestOrId.short_id || getShortId(digestOrId.id);
  const slug = generateDigestSlug(digestOrId.project.name);
  return `${baseUrl}/digest/${shortId}/${slug}`;
}

/**
 * Generates the internal navigation URL for a paper within a digest
 * Uses canonical format: /digest/{digestShortId}/{digestSlug}/p/{paperShortId}/{paperSlug}
 */
export function getDigestPaperUrl(
    digest: DigestUrlInput,
    projectPaper: ProjectPaperUrlInput
): string {
    const digestShortId = digest.short_id || getShortId(digest.id);
    const digestSlug = generateDigestSlug(digest.project.name);
    const paperShortId = projectPaper.short_id || getShortId(projectPaper.id);
    const paperSlug = projectPaper.slug || (projectPaper.paper_title ? createSlug(projectPaper.paper_title) : 'paper');

    return `/digest/${digestShortId}/${digestSlug}/p/${paperShortId}/${paperSlug}`;
}

/**
 * Generates digest paper URL params for expo-router navigation
 * Uses canonical format with paper slug
 */
export function getDigestPaperUrlParams(
    digest: DigestUrlInput,
    projectPaper: ProjectPaperUrlInput
) {
    const digestShortId = digest.short_id || getShortId(digest.id);
    const digestSlug = generateDigestSlug(digest.project.name);
    const paperShortId = projectPaper.short_id || getShortId(projectPaper.id);
    const paperSlug = projectPaper.slug || (projectPaper.paper_title ? createSlug(projectPaper.paper_title) : 'paper');

    return {
        pathname: '/digest/[issueId]/[...slug]/p/[paperId]/[...paperSlug]',
        params: {
            issueId: digestShortId,
            slug: digestSlug,
            paperId: paperShortId,
            paperSlug: paperSlug
        }
    };
}

/**
 * Generates the external shareable URL for a paper within a digest
 * Uses canonical format: /digest/{digestShortId}/{digestSlug}/p/{paperShortId}/{paperSlug}
 *
 * Overloaded signatures:
 * 1. (digest, projectPaper) - Modern signature with full objects
 * 2. (issueId, paperId, digestSlug?) - Legacy signature for backward compatibility
 */
export function getDigestPaperShareUrl(
    digestOrIssueId: DigestUrlInput | string,
    projectPaperOrPaperId: ProjectPaperUrlInput | string,
    digestSlug?: string
): string {
    const baseUrl = process.env.EXPO_PUBLIC_BASE_URL || 'https://paperzilla.ai';

    // Legacy signature: (issueId, paperId, digestSlug?)
    if (typeof digestOrIssueId === 'string') {
        const issueId = digestOrIssueId;
        const paperId = projectPaperOrPaperId as string;
        const shortIssueId = issueId.includes('-') ? getShortId(issueId) : issueId;
        if (digestSlug) {
            return `${baseUrl}/digest/${shortIssueId}/${digestSlug}/p/${paperId}`;
        }
        return `${baseUrl}/digest/${shortIssueId}/p/${paperId}`;
    }

    // Modern signature: (digest, projectPaper)
    const digest = digestOrIssueId;
    const projectPaper = projectPaperOrPaperId as ProjectPaperUrlInput;
    const digestShortId = digest.short_id || getShortId(digest.id);
    const slug = generateDigestSlug(digest.project.name);
    const paperShortId = projectPaper.short_id || getShortId(projectPaper.id);
    const paperSlug = projectPaper.slug || (projectPaper.paper_title ? createSlug(projectPaper.paper_title) : 'paper');

    return `${baseUrl}/digest/${digestShortId}/${slug}/p/${paperShortId}/${paperSlug}`;
}

/**
 * Generates digest URL params for expo-router navigation
 * Accepts either a digest object or individual params
 */
export function getDigestUrlParams(digestOrId: DigestUrlInput | string, projectName?: string) {
  if (typeof digestOrId === 'string') {
    // Legacy: called with (issueId, projectName)
    const shortId = getShortId(digestOrId);
    const slug = generateDigestSlug(projectName!);
    return {
      pathname: '/digest/[issueId]/[...slug]',
      params: {
        issueId: shortId,
        slug
      }
    };
  }

  // Modern: called with digest object
  const shortId = digestOrId.short_id || getShortId(digestOrId.id);
  const slug = generateDigestSlug(digestOrId.project.name);
  return {
    pathname: '/digest/[issueId]/[...slug]',
    params: {
      issueId: shortId,
      slug
    }
  };
}
